import { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { getTags } from '../../api/services/Rides';

export const RIDES_TAGS_QUERY_KEY = 'rides-tags';

export const TAGS_PER_PAGE = 10;

interface UseRidesTagsParams {
  enabled: boolean;
  query?: string;
}

export const useRidesTags = ({ enabled, query }: UseRidesTagsParams) => {
  const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [RIDES_TAGS_QUERY_KEY, query],
    queryFn: async ({ pageParam, signal }) => {
      const response = await getTags({ page: pageParam, perPage: TAGS_PER_PAGE, ...(query && { q: query }) }, signal);

      if (response.type === 'error') {
        throw new Error(response.error.errors[0] ?? 'Failed to load tags');
      }

      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.reduce((count, page) => count + (page.tags?.length ?? 0), 0);

      return fetchedCount < (lastPage.meta?.totalCount ?? fetchedCount) ? allPages.length + 1 : undefined;
    },
    enabled,
  });

  const { data: baselineData } = useQuery({
    queryKey: [RIDES_TAGS_QUERY_KEY, 'baseline'],
    queryFn: async ({ signal }) => {
      const response = await getTags({ page: 1, perPage: TAGS_PER_PAGE }, signal);

      if (response.type === 'error') {
        throw new Error(response.error.errors[0] ?? 'Failed to load tags');
      }

      return response.data;
    },
    enabled: enabled && !!query,
  });

  const tags = useMemo(() => data?.pages.flatMap((page) => page.tags ?? []) ?? [], [data]);
  const totalCount = data?.pages[0]?.meta?.totalCount ?? tags.length;
  const baselineTotalCount = baselineData?.meta?.totalCount ?? totalCount;

  return {
    tags,
    totalCount,
    baselineTotalCount,
    isFetching,
    hasMore: hasNextPage ?? false,
    fetchNextPage,
  };
};
